import { useState } from "react";

// iter-66/67: Shared deposit-count bucket picker.
// Used by UPI channels + payment link modals. Presets are locked to
// the 5-chip taxonomy the routing engine already understands (0-4);
// custom values (≥ 5) render as removable pills, and a bulk paste
// input accepts CSV like "5,7,10" to add many buckets at once.
//
// iter-67 adds an optional "N and above" threshold rendered as an
// extra chip next to the presets. Pass `threshold` + `onThresholdChange`
// to enable it; when omitted the picker degrades gracefully to the
// original discrete-only behaviour.
//
// Props:
//   value:              number[]           currently selected deposit-count indexes
//   onChange            fn(next[])         called with the new sorted array
//   threshold:          number|null        optional 1-indexed "N and above" min
//   onThresholdChange:  fn(n|null)         called when threshold changes; omit to hide chip
//   testId              string             stable id prefix for data-testids

export const CAT_LABEL = { 0: "FTD", 1: "2nd", 2: "3rd", 3: "4th", 4: "5+" };

export function catLabel(n) {
    if (n in CAT_LABEL) return CAT_LABEL[n];
    const ord = n + 1;
    const s = ["th", "st", "nd", "rd"];
    const v = ord % 100;
    return ord + (s[(v - 20) % 10] || s[v] || s[0]);
}

export function fmtCategories(arr, threshold) {
    const list = (arr || []).map(catLabel);
    if (threshold != null && Number(threshold) >= 1) list.push(`${threshold}+ (and above)`);
    return list.length ? list.join(", ") : "—";
}

export default function DepositCountPicker({ value = [], onChange, threshold = null, onThresholdChange, testId = "dcat" }) {
    const [showCustom, setShowCustom] = useState(false);
    const [customVal, setCustomVal] = useState("");
    const [showBulk, setShowBulk] = useState(false);
    const [bulkVal, setBulkVal] = useState("");
    const [showThr, setShowThr] = useState(false);
    const [thrVal, setThrVal] = useState("");
    const [err, setErr] = useState("");
    const thrEnabled = typeof onThresholdChange === "function";

    const toggle = (n) => {
        const s = new Set(value);
        s.has(n) ? s.delete(n) : s.add(n);
        onChange([...s].sort((a, b) => a - b));
    };

    const addCustom = () => {
        setErr("");
        const n = parseInt(customVal, 10);
        if (!Number.isFinite(n) || n < 1 || n > 9999) {
            setErr("Enter a whole number between 1 and 9999");
            return;
        }
        const idx = n - 1;
        if (idx <= 4) {
            setErr(`Use the preset "${catLabel(idx)}" chip for that bucket`);
            return;
        }
        if (value.includes(idx)) { setShowCustom(false); setCustomVal(""); return; }
        onChange([...value, idx].sort((a, b) => a - b));
        setShowCustom(false); setCustomVal("");
    };

    // Bulk parse: accepts "5,7,10" / "5 7 10" / "5;7;10" / mixed
    // whitespace. Silently dedupes, ignores non-numeric tokens, and
    // clamps out-of-range values so a paste of "5,7,foo,-2,10000" still
    // yields a clean [4,6,9999] delta.
    const applyBulk = () => {
        setErr("");
        const tokens = bulkVal.split(/[\s,;]+/).map((t) => t.trim()).filter(Boolean);
        const merged = new Set(value);
        let added = 0, skipped = 0;
        for (const t of tokens) {
            const n = parseInt(t, 10);
            if (!Number.isFinite(n) || n < 1 || n > 9999) { skipped++; continue; }
            const idx = n - 1;
            if (!merged.has(idx)) { merged.add(idx); added++; } else { skipped++; }
        }
        onChange([...merged].sort((a, b) => a - b));
        setShowBulk(false); setBulkVal("");
        if (added === 0 && skipped > 0) {
            setErr(`No new buckets added (${skipped} skipped — duplicates or out of range)`);
        }
    };

    return (
        <div>
            <div className="flex gap-2 flex-wrap items-center">
                {[0, 1, 2, 3, 4].map((n) => (
                    <button data-testid={`${testId}-${n}`} type="button" key={n} onClick={() => toggle(n)}
                            className={`px-3 py-1.5 rounded-md text-xs font-medium border ${
                                value.includes(n)
                                    ? "bg-blue-50 border-blue-200 text-blue-700"
                                    : "bg-white border-slate-200 text-slate-500"
                            }`}>
                        {n === 4 ? "5+ (repeat)" : n === 0 ? "FTD (1st)" : `${n + 1}${["st","nd","rd","th","th"][n]}`}
                    </button>
                ))}

                {value.filter((n) => n >= 5).map((n) => (
                    <span key={`c-${n}`}
                          data-testid={`${testId}-custom-${n}`}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium border bg-blue-50 border-blue-200 text-blue-700">
                        {catLabel(n)}
                        <button type="button" aria-label={`Remove ${catLabel(n)}`}
                                onClick={() => toggle(n)}
                                className="text-blue-500 hover:text-blue-800 -mr-1 pl-1 leading-none">×</button>
                    </span>
                ))}

                {!showCustom && !showBulk && !showThr && (
                    <>
                        <button type="button" data-testid={`${testId}-add`}
                                onClick={() => { setShowCustom(true); setErr(""); }}
                                className="px-3 py-1.5 rounded-md text-xs font-medium border bg-white border-dashed border-slate-300 text-slate-500 hover:border-slate-400 hover:text-slate-700">
                            + Custom
                        </button>
                        <button type="button" data-testid={`${testId}-bulk`}
                                onClick={() => { setShowBulk(true); setErr(""); }}
                                className="px-3 py-1.5 rounded-md text-xs font-medium border bg-white border-dashed border-slate-300 text-slate-500 hover:border-slate-400 hover:text-slate-700">
                            + Paste list
                        </button>
                        {thrEnabled && threshold == null && (
                            <button type="button" data-testid={`${testId}-thr-add`}
                                    onClick={() => { setShowThr(true); setThrVal(""); setErr(""); }}
                                    className="px-3 py-1.5 rounded-md text-xs font-medium border bg-white border-dashed border-emerald-300 text-emerald-700 hover:border-emerald-400 hover:bg-emerald-50">
                                + N and above
                            </button>
                        )}
                    </>
                )}

                {thrEnabled && threshold != null && (
                    <span data-testid={`${testId}-thr-chip`}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium border bg-emerald-50 border-emerald-200 text-emerald-800">
                        {threshold}+ (and above)
                        <button type="button" data-testid={`${testId}-thr-clear`}
                                aria-label="Remove threshold"
                                onClick={() => onThresholdChange(null)}
                                className="text-emerald-600 hover:text-emerald-900 -mr-1 pl-1 leading-none">×</button>
                    </span>
                )}

                {showCustom && (
                    <div className="inline-flex items-center gap-1">
                        <input type="number" min="1" max="9999" autoFocus
                               data-testid={`${testId}-custom-input`}
                               placeholder="e.g. 10" value={customVal}
                               onChange={(e) => setCustomVal(e.target.value)}
                               onKeyDown={(e) => {
                                   if (e.key === "Enter") { e.preventDefault(); addCustom(); }
                                   if (e.key === "Escape") { setShowCustom(false); setCustomVal(""); }
                               }}
                               className="input font-mono w-24 py-1 text-xs" />
                        <button type="button" data-testid={`${testId}-custom-add`} onClick={addCustom}
                                className="px-2 py-1 rounded-md text-xs font-medium bg-blue-600 text-white hover:bg-blue-700">Add</button>
                        <button type="button" onClick={() => { setShowCustom(false); setCustomVal(""); }}
                                className="px-2 py-1 rounded-md text-xs font-medium text-slate-500 hover:text-slate-700">×</button>
                    </div>
                )}

                {showBulk && (
                    <div className="inline-flex items-center gap-1">
                        <input type="text" autoFocus
                               data-testid={`${testId}-bulk-input`}
                               placeholder="5, 7, 10, 25"
                               value={bulkVal}
                               onChange={(e) => setBulkVal(e.target.value)}
                               onKeyDown={(e) => {
                                   if (e.key === "Enter") { e.preventDefault(); applyBulk(); }
                                   if (e.key === "Escape") { setShowBulk(false); setBulkVal(""); }
                               }}
                               className="input font-mono w-56 py-1 text-xs" />
                        <button type="button" data-testid={`${testId}-bulk-apply`} onClick={applyBulk}
                                className="px-2 py-1 rounded-md text-xs font-medium bg-blue-600 text-white hover:bg-blue-700">Add all</button>
                        <button type="button" onClick={() => { setShowBulk(false); setBulkVal(""); }}
                                className="px-2 py-1 rounded-md text-xs font-medium text-slate-500 hover:text-slate-700">×</button>
                    </div>
                )}

                {thrEnabled && showThr && (
                    <div className="inline-flex items-center gap-1">
                        <input type="number" min="1" max="9999" autoFocus
                               data-testid={`${testId}-thr-input`}
                               placeholder="e.g. 5"
                               value={thrVal}
                               onChange={(e) => setThrVal(e.target.value)}
                               onKeyDown={(e) => {
                                   if (e.key === "Enter") {
                                       e.preventDefault();
                                       const n = parseInt(thrVal, 10);
                                       if (!Number.isFinite(n) || n < 1 || n > 9999) {
                                           setErr("Enter a whole number between 1 and 9999");
                                           return;
                                       }
                                       onThresholdChange(n);
                                       setShowThr(false); setThrVal(""); setErr("");
                                   }
                                   if (e.key === "Escape") { setShowThr(false); setThrVal(""); }
                               }}
                               className="input font-mono w-24 py-1 text-xs" />
                        <span className="text-[11px] text-slate-500">and above</span>
                        <button type="button" data-testid={`${testId}-thr-apply`}
                                onClick={() => {
                                    const n = parseInt(thrVal, 10);
                                    if (!Number.isFinite(n) || n < 1 || n > 9999) {
                                        setErr("Enter a whole number between 1 and 9999");
                                        return;
                                    }
                                    onThresholdChange(n);
                                    setShowThr(false); setThrVal(""); setErr("");
                                }}
                                className="px-2 py-1 rounded-md text-xs font-medium bg-emerald-600 text-white hover:bg-emerald-700">Set</button>
                        <button type="button" onClick={() => { setShowThr(false); setThrVal(""); }}
                                className="px-2 py-1 rounded-md text-xs font-medium text-slate-500 hover:text-slate-700">×</button>
                    </div>
                )}
            </div>
            {err && <p className="text-[11px] text-rose-600 mt-1.5">{err}</p>}
            <p className="text-[11px] text-slate-400 mt-1.5">
                Presets cover deposits 1–4 and 5+. Use <span className="font-mono">+ Custom</span> for a
                single specific index (e.g. 10 = 10th deposit) or <span className="font-mono">+ Paste list</span> to
                drop several at once like <span className="font-mono">5, 7, 10, 25</span>.
                {thrEnabled && (
                    <>
                        {" "}<span className="font-mono">+ N and above</span> adds an open-ended threshold
                        (e.g. 10 = 10th deposit onwards) that stacks on top of the exact list.
                    </>
                )}
            </p>
        </div>
    );
}
